import React from 'react';

import { VOID_FUNCTION } from 'constants/index';
import { DISCOUNTS } from 'store/carInstance/constants';
import { convertPriceToString } from 'utils/convertPrice';
import { InputMask } from 'utils/inputMask';

import { FIELDS_NAME } from './BlockMain.constants';
import { withNamedCheckbox } from './BlockMain.hocs';
import { useBlockMainPresenter } from './BlockMain.presenter';
import * as S from './BlockMain.styles';

const DiscountCheckBox = withNamedCheckbox(S.DiscountCheckBox, FIELDS_NAME.IS_SET_DISCOUNT_MFR);
const DiscountDirectCheckBox = withNamedCheckbox(
  S.DiscountDirectCheckBox,
  FIELDS_NAME.IS_SET_DISCOUNT_DIRECT
);

export const BlockMain = ({
  allFetch,
  brands,
  brandsFetch,
  carInfoFetch,
  carInstance,
  creditBankId,
  creditBanksList,
  disabled = false,
  disableFields,
  errors,
  hiddenFields,
  isInCredit,
  mileage,
  models,
  modelsFetch,
  onBankChange = VOID_FUNCTION,
  onBlur = VOID_FUNCTION,
  onChange = VOID_FUNCTION,
  onChangeCarModel = VOID_FUNCTION,
  onClickFullCalc = null,
  onClickIsInCredit = VOID_FUNCTION,
  onPatch = VOID_FUNCTION,
  onResetErrors = VOID_FUNCTION,
  onScroll = VOID_FUNCTION,
  onSetCarInstance = VOID_FUNCTION,
  onSetYear = VOID_FUNCTION,
  onValidate = VOID_FUNCTION,
  patchIsNew,
  requiredFields = [],
  scrollTo,
  setImage = VOID_FUNCTION,
  setMileage,
  showRestrictions = false,
  versions,
  versionsFetch,
}) => {
  const presenter = useBlockMainPresenter({
    allFetch,
    brands,
    brandsFetch,
    carInfoFetch,
    carInstance,
    disabled,
    disableFields,
    hiddenFields,
    isInCredit,
    models,
    modelsFetch,
    onBlur,
    onChange,
    onChangeCarModel,
    onClickFullCalc,
    onClickIsInCredit,
    onPatch,
    onResetErrors,
    onScroll,
    onSetCarInstance,
    onSetYear,
    onValidate,
    patchIsNew,
    requiredFields,
    scrollTo,
    setImage,
    setMileage,
    showRestrictions,
    versions,
    versionsFetch,
  });

  return (
    <>
      <S.GridStyled moreRow={presenter.moreRow} ref={presenter.ref}>
        <S.Brands
          defaultValue={presenter.brand}
          disabled={presenter.fetchBrands || disabled}
          error={errors[FIELDS_NAME.BRAND]}
          fetch={presenter.fetchBrands}
          id={FIELDS_NAME.BRAND}
          label="Марка"
          list={brands}
          name={FIELDS_NAME.BRAND}
          onlyid={false}
          required={presenter.required[FIELDS_NAME.BRAND]}
          value={presenter.brand}
          search
          onChange={presenter.onChangeBrand}
        />
        <S.Models
          defaultValue={presenter.model}
          disabled={presenter.fetchModels || !presenter.brand?.value || disabled}
          error={errors[FIELDS_NAME.MODEL]}
          fetch={presenter.fetchModels}
          id={FIELDS_NAME.MODEL}
          label="Модель"
          list={models}
          name={FIELDS_NAME.MODEL}
          onlyid={false}
          required={presenter.required[FIELDS_NAME.MODEL]}
          value={presenter.model}
          search
          onChange={presenter.onChangeModel}
        />
        {!presenter.hidden[FIELDS_NAME.VERSION] && (
          <S.Config
            defaultValue={presenter.version}
            disabled={
              presenter.fetchVersions ||
              !presenter.model?.value ||
              disabled ||
              disableFields.includes('version')
            }
            error={errors[FIELDS_NAME.VERSION]}
            fetch={presenter.fetchVersions}
            id={FIELDS_NAME.VERSION}
            label="Комплектация"
            list={versions}
            name={FIELDS_NAME.VERSION}
            onlyid={false}
            value={presenter.version}
            onChange={presenter.onChangeVersion}
          />
        )}
        <S.Year
          defaultValue={presenter.yearId}
          disabled={disabled}
          error={errors[FIELDS_NAME.YEAR]}
          id={FIELDS_NAME.YEAR}
          label="Год выпуска"
          list={presenter.yearList}
          name={FIELDS_NAME.YEAR}
          onlyid={false}
          required={presenter.required[FIELDS_NAME.YEAR]}
          value={presenter.yearId}
          onChange={presenter.onChangeYear}
        />
        {!presenter.hidden[FIELDS_NAME.PRICE] && (
          <S.Price
            dataTest="carPrice"
            defaultValue={presenter.selectedPrice}
            disabled={disabled}
            error={errors[FIELDS_NAME.PRICE]}
            id={FIELDS_NAME.PRICE}
            label="Стоимость автомобиля"
            name={FIELDS_NAME.PRICE}
            required={presenter.required[FIELDS_NAME.PRICE]}
            value={presenter.selectedPrice}
            onBlur={presenter.onBlurPrice}
            onChange={presenter.onChangePrice}
          />
        )}
        {!presenter.hidden[FIELDS_NAME.OFFER_PRICE] && (
          <S.OfferPrice
            dataTest="carOfferPrice"
            defaultValue={presenter.offerPrice}
            disabled={true}
            id={FIELDS_NAME.OFFER_PRICE}
            label="Стоимость авто с учётом скидки"
            name={FIELDS_NAME.OFFER_PRICE}
            value={presenter.offerPrice}
          />
        )}
        {!presenter.hidden[FIELDS_NAME.MILEAGE] && !carInstance.isNew && (
          <S.Mileage
            dataTest="mileage"
            disabled={disabled}
            error={errors[FIELDS_NAME.MILEAGE]}
            id={FIELDS_NAME.MILEAGE}
            label="Пробег, км"
            mask={InputMask.MILEAGE}
            name={FIELDS_NAME.MILEAGE}
            required={presenter.required[FIELDS_NAME.MILEAGE]}
            val={mileage}
            isControlledComponent
            onBlur={presenter.onBlurMileage}
            onChange={presenter.onChangeMileage}
          />
        )}
        {!presenter.hidden[FIELDS_NAME.FULL_CALCULATION] && (
          <S.FullCalculationCheckBox
            borderRadius={5}
            disabled
            label={<span>Есть данные VIN и ПТС/ЭПТС</span>}
            name={FIELDS_NAME.FULL_CALCULATION}
            size={19}
            checked
          />
        )}
        {!presenter.hidden[FIELDS_NAME.IS_SET_DISCOUNT_MFR] && (
          <DiscountCheckBox
            dataTest="discount-mfr"
            disabled={disabled}
            value={carInstance.discountType === DISCOUNTS.mfr}
            onChange={presenter.onChangeIsSetDiscountMfr}
          >
            <span>
              {'Скидка производителя '}
              <span style={{ whiteSpace: 'nowrap' }}>
                {convertPriceToString(presenter.version.discount)} ₽
              </span>
            </span>
          </DiscountCheckBox>
        )}
        {!presenter.hidden[FIELDS_NAME.IS_SET_DISCOUNT_DIRECT] && (
          <DiscountDirectCheckBox
            dataTest="discount-direct"
            dataTestLabel={`discount-direct-container-${carInstance.discountType === DISCOUNTS.direct}`}
            disabled={disabled}
            value={carInstance.discountType === DISCOUNTS.direct}
            onChange={presenter.onChangeIsSetDiscountDirect}
          >
            <span>
              {'Скидка Direct '}
              <span style={{ whiteSpace: 'nowrap' }}>
                {convertPriceToString(presenter.version.discountDirect)} ₽
              </span>
            </span>
          </DiscountDirectCheckBox>
        )}
        {!presenter.hidden[FIELDS_NAME.IS_IN_CREDIT] && (
          <S.CreditCheckBox
            borderRadius={5}
            checked={isInCredit}
            disabled={disabled}
            label={<span style={{ fontSize: 15 }}>Авто в кредит</span>}
            name={FIELDS_NAME.IS_IN_CREDIT}
            size={19}
            onClick={onClickIsInCredit}
          />
        )}
        {!presenter.hidden[FIELDS_NAME.BANK] && (
          <S.CreditBanks
            disabled={disabled}
            error={errors.selectedBank}
            id={FIELDS_NAME.BANK}
            label="Банк"
            list={creditBanksList}
            name={FIELDS_NAME.BANK}
            onlyid={false}
            required={true}
            value={creditBankId}
            search
            onChange={onBankChange}
          />
        )}
      </S.GridStyled>
    </>
  );
};
